(() => {
  'use strict';
  if (window.LRSVehicleReveal) return;
  const instances = new Map();
  const hoverLayout = window.matchMedia('(min-width: 701px) and (hover: hover) and (pointer: fine)');

  function init(root = document) {
    const sections = [...root.querySelectorAll('[data-vehicle-reveal="true"]')];
    if (root.matches?.('[data-vehicle-reveal="true"]')) sections.unshift(root);
    sections.forEach(section => {
      if (instances.has(section)) return;
      const cards = [...section.querySelectorAll('[data-vehicle-card]')];
      if (!cards.length) return;
      const abort = new AbortController();
      const listen = (target, name, handler, options = {}) => target.addEventListener(name, handler, {...options, signal: abort.signal});
      const seen = new Set();
      const images = new Map();
      let observer = null;
      let mode = '';
      let resizeFrame = 0;
      let animationFrame = 0;

      cards.forEach(card => {
        const image = card.querySelector('.vehicle-card-image img') || card.querySelector(':scope > img');
        const imageArea = card.querySelector('.vehicle-card-image') || image;
        if (image) images.set(image, card);
        // A text-only archive entry remains visible in every input mode.
        else seen.add(card);
        if (imageArea) listen(imageArea, 'pointerenter', event => {
          if (mode === 'hover' && event.pointerType !== 'touch') card.classList.add('is-info-visible');
        });
        listen(card, 'pointerleave', () => {
          if (mode === 'hover' && image) card.classList.remove('is-info-visible');
        });
        // Hybrid devices can still reveal the copy with a touch, before following a link.
        listen(card, 'pointerdown', event => {
          if (event.pointerType === 'touch') card.classList.add('is-info-visible');
        }, {passive: true});
      });

      function observeImages() {
        observer?.disconnect();
        observer = null;
        if (mode !== 'viewport') return;
        if (!('IntersectionObserver' in window)) {
          cards.forEach(card => { seen.add(card); card.classList.add('is-info-visible'); });
          return;
        }
        const targets = [...images].filter(([, card]) => !seen.has(card));
        if (!targets.length) return;
        const thresholds = new Map(targets.map(([image]) => {
          const height = image.getBoundingClientRect().height || image.height || 1;
          // One quarter of the visible screen also works with unusually tall photos.
          return [image, Math.min(.25, Math.max(.001, window.innerHeight * .25 / height))];
        }));
        observer = new IntersectionObserver(entries => {
          if (mode !== 'viewport') return;
          entries.forEach(entry => {
            if (!entry.isIntersecting || entry.intersectionRatio + .001 < thresholds.get(entry.target)) return;
            const card = images.get(entry.target);
            seen.add(card);
            card.classList.add('is-info-visible');
            observer?.unobserve(entry.target);
          });
        }, {threshold: [...new Set([0, ...thresholds.values()])]});
        targets.forEach(([image]) => observer.observe(image));
      }

      function configure() {
        mode = hoverLayout.matches ? 'hover' : 'viewport';
        section.dataset.vehicleRevealMode = mode;
        cards.forEach(card => {
          const image = card.querySelector('.vehicle-card-image img') || card.querySelector(':scope > img');
          const imageArea = card.querySelector('.vehicle-card-image') || image;
          const visible = !image || (mode === 'viewport' ? seen.has(card) : imageArea.matches(':hover'));
          card.classList.toggle('is-info-visible', visible);
        });
        observeImages();
      }

      configure();
      section.classList.add('is-vehicle-reveal-ready');
      animationFrame = requestAnimationFrame(() => section.classList.add('is-vehicle-reveal-animated'));
      listen(hoverLayout, 'change', configure);
      listen(window, 'resize', () => {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(observeImages);
      }, {passive: true});
      listen(section, 'shopify:block:select', event => {
        const card = event.target.closest('[data-vehicle-card]') || event.target.querySelector('[data-vehicle-card]');
        card?.classList.add('is-editor-selected');
      });
      listen(section, 'shopify:block:deselect', event => {
        const card = event.target.closest('[data-vehicle-card]') || event.target.querySelector('[data-vehicle-card]');
        card?.classList.remove('is-editor-selected');
      });

      instances.set(section, () => {
        abort.abort();
        observer?.disconnect();
        cancelAnimationFrame(resizeFrame);
        cancelAnimationFrame(animationFrame);
        section.classList.remove('is-vehicle-reveal-ready', 'is-vehicle-reveal-animated');
        delete section.dataset.vehicleRevealMode;
        cards.forEach(card => card.classList.remove('is-info-visible', 'is-editor-selected'));
      });
    });
  }

  function destroy(root) {
    instances.forEach((cleanup, section) => {
      if (root === section || root.contains(section)) { cleanup(); instances.delete(section); }
    });
  }

  window.LRSVehicleReveal = {init, destroy};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
})();
